import { CalendarClock } from 'lucide-react';

import { LineChart } from '@/components/Chart/LineChart';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import {
    type ClientCycle,
    type ClientsParams,
    clientsExportUrl,
    type ClientsReport,
} from '@/features/reports/api';
import { exportFileName, monthsText, seasonsText } from '@/features/reports/clients/labels';
import { ClientName } from '@/features/reports/clients/SpendView';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { Definition, ReportCard, Tile } from '@/features/reports/parts';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { formatDate } from '@/lib/format';

const COPY = CLIENTS_COPY.cycles;

/**
 * Clients › Cycles (ALS-87): each client's peak spending season, so a stylist reaches out as it
 * starts (the client on 20 May; Franka, 22 Oct 2026). The main list is whose season starts in the
 * coming weeks; under it, every client's usual buying months and usual gap between orders. Above
 * them, the company-wide monthly line: the last twelve months against the twelve before.
 */
export function CyclesView({ report, params }: { report: ClientsReport; params: ClientsParams }) {
    const cycles = report.cycles;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <Tile
                    label={COPY.tiles.soon}
                    value={String(cycles.startingSoon.length)}
                    note={COPY.tiles.soonNote(cycles.lookaheadDays)}
                    definition={COPY.definition}
                />
                <Tile
                    label={COPY.tiles.gap}
                    value={COPY.tiles.gapValue(cycles.medianGapDays)}
                    note={COPY.tiles.gapNote}
                />
            </div>

            <ReportCard title={COPY.chart.title} subtitle={COPY.chart.subtitle}>
                <LineChart
                    title={COPY.chart.title}
                    labels={cycles.recent.map((p) => p.label)}
                    values={cycles.recent.map((p) => p.spend)}
                    seriesLabel={COPY.chart.recent}
                    comparison={{
                        label: COPY.chart.before,
                        values: cycles.yearBefore.map((p) => p.spend),
                    }}
                    format={formatPoundsCompact}
                />
            </ReportCard>

            <ReportCard
                title={COPY.soon.card}
                subtitle={COPY.soon.subtitle(cycles.lookaheadDays)}
                actions={
                    <>
                        <Definition label={COPY.soon.card}>{COPY.definition}</Definition>
                        <ExportButton
                            url={clientsExportUrl('PeakSeason', params)}
                            fileName={exportFileName(['peak-season'])}
                            disabled={cycles.startingSoon.length === 0}
                        />
                    </>
                }
            >
                {cycles.startingSoon.length === 0 ? (
                    <EmptyState icon={<CalendarClock />} title={COPY.soon.empty} />
                ) : (
                    <CycleTable rows={cycles.startingSoon} region={COPY.soon.card} starts />
                )}
            </ReportCard>

            <ReportCard
                title={COPY.months.card}
                subtitle={COPY.months.subtitle}
                actions={
                    <ExportButton
                        url={clientsExportUrl('BuyingMonths', params)}
                        fileName={exportFileName(['buying-months'])}
                        disabled={cycles.clients.length === 0}
                    />
                }
            >
                {cycles.clients.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.months.empty}</p>
                ) : (
                    <CycleTable rows={cycles.clients} region={COPY.months.card} />
                )}
            </ReportCard>
        </>
    );
}

/** One client per row: her season, when it starts or which months she buys in, and her usual gap. */
function CycleTable({
    rows,
    region,
    starts = false,
}: {
    rows: ClientCycle[];
    region: string;
    starts?: boolean;
}) {
    const C = COPY.columns;
    return (
        <Table stack region={region} className="table-stack--wide-labels">
            <TableHeader>
                <TableRow>
                    <TableHead>{C.client}</TableHead>
                    <TableHead>{starts ? C.starts : C.months}</TableHead>
                    <TableHead>{C.season}</TableHead>
                    <TableHead className="text-right">{C.gap}</TableHead>
                    <TableHead>{C.lastOrder}</TableHead>
                    <TableHead className="text-right">{C.lifetime}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((c) => (
                    <TableRow key={c.customerId}>
                        <TableCell data-stack="title">
                            <ClientName
                                customerId={c.customerId}
                                name={c.client}
                                manager={c.relationshipManager}
                            />
                        </TableCell>
                        {starts ? (
                            <TableCell
                                data-stack="status"
                                className="font-medium whitespace-nowrap"
                            >
                                {c.nextSeasonOn ? formatDate(c.nextSeasonOn) : '–'}
                            </TableCell>
                        ) : (
                            <TableCell data-stack="meta" data-label={C.months}>
                                {monthsText(c.usualMonths)}
                            </TableCell>
                        )}
                        <TableCell data-stack="meta" data-label={C.season}>
                            {seasonsText(c.seasons)}
                        </TableCell>
                        <TableNum data-stack="meta" data-label={C.gap}>
                            {COPY.gapDays(c.usualGapDays)}
                        </TableNum>
                        <TableCell
                            data-stack="meta"
                            data-label={C.lastOrder}
                            className="text-muted-foreground whitespace-nowrap"
                        >
                            {formatDate(c.lastOrderOn)}
                        </TableCell>
                        <TableNum data-stack="meta" data-label={C.lifetime}>
                            {formatPounds(c.lifetime)}
                        </TableNum>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
